import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import useQueryParams from '../hooks/useQueryParams'
import AboutPage from '../pages/AboutPage'
import AddInstagramErrorPage from '../pages/accountOnboarding/AddInstagramErrorPage'
import AuthoriseResponsePage from '../pages/accountOnboarding/AuthoriseResponsePage'
import NewFacebookAccountPage from '../pages/accountOnboarding/NewFacebookAccountPage'
import NewInstagramAccountFacebookAuthPage from '../pages/accountOnboarding/NewInstagramAccountFacebookAuthPage'
import NewTwitterAccountPage from '../pages/accountOnboarding/NewTwitterAccountPage'
import NewMonitoredAccountPage from '../pages/accountOnboarding/NewMonitoredAccountPage'
import NewYouTubeAccountPage from '../pages/accountOnboarding/NewYouTubeAccountPage'
import AccountPage from '../pages/AccountPage'
import FetchHistoricContentPage from '../pages/FetchHistoricContentPage'
import ModerationBoundariesPage from '../pages/ModerationBoundariesPage'
import NotFoundPage from '../pages/NotFoundPage'
import SupportPage from '../pages/SupportPage'
import TeamPage from '../pages/TeamPage'
import { isUserAdminState, userState } from '../store/DomainStore'
import AdminLayout from './AdminLayout'
import Sidebar from './Sidebar'

import { useEffect } from 'react'
import { Tooltip } from 'react-tooltip'
import EnhancedTeamDetailsForm from '../components/team/EnhancedTeamDetailsForm'
import AccountAuthorisationPage from '../pages/AccountAuthorisationPage'
import AuthorisationFailurePage from '../pages/accountOnboarding/AuthorisationFailurePage'
import NewAccountAuthorisedPage from '../pages/accountOnboarding/NewAccountAuthorisedPage'
import NewAccountPage from '../pages/accountOnboarding/NewAccountPage'
import NewInstagramAccountPage from '../pages/accountOnboarding/NewInstagramAccountPage'
import NewTikTokAccountPage from '../pages/accountOnboarding/NewTikTokAccountPage'
import AccountSettingsPage from '../pages/AccountSettingsPage'
import AccountShadowBanningSettingsPage from '../pages/AccountShadowBanningSettingsPage'
import AccountValidationPage from '../pages/AccountValidationPage'
import CommenterListPage from '../pages/admin/CommenterListPage'
import MetaAdVisibilityPage from '../pages/admin/MetaAdVisibilityPage'
import KeywordCreatePage from '../pages/admin/KeywordCreatePage'
import LlmReplySettingsPage from '../pages/admin/prompt/LlmReplySettingsPage'
import SpamModelPage from '../pages/admin/spam/SpamModelPage'
import TeamEditPage from '../pages/admin/TeamEditPage'
import TeamListPage from '../pages/admin/TeamListPage'
import UserCreatePage from '../pages/admin/UserCreatePage'
import UserEditPage from '../pages/admin/UserEditPage'
import UserEventsPage from '../pages/admin/UserEventsPage'
import UserListPage from '../pages/admin/UserListPage'
import ServiceHealthPage from '../pages/admin/ServiceHealthPage'
import CookieListPage from '../pages/admin/CookieListPage'
import CookieFormPage from '../pages/admin/CookieFormPage'
import ReferrerListPage from '../pages/admin/ReferrerListPage'
import ScreenshotListPage from '../pages/admin/ScreenshotListPage'
import PostListPage from '../pages/admin/post/PostListPage'
import PostDetailPage from '../pages/admin/post/PostDetailPage'
import AdminClassifierLibraryPage from '../pages/admin/ClassifierLibraryPage'
import CreateClassifierPage from '../pages/admin/CreateClassifierPage'
import AdminClassifierEditPage from '../pages/admin/ClassifierEditPage'
// Wireframe prototype screens surfaced in the real IA for stakeholder review.
// Test-only branch — not intended to ship as live functionality.
import WireframesHubPage from '../pages/wireframes/WireframesHubPage'
import ClassifierLibraryPage from '../pages/wireframes/ClassifierLibraryPage'
import ClassifierEditPage from '../pages/wireframes/ClassifierEditPage'
import OrgListPage from '../pages/wireframes/OrgListPage'
import OrgConfigPage from '../pages/wireframes/OrgConfigPage'
import ProfileConfigPage from '../pages/wireframes/ProfileConfigPage'
import TeamConfigPage from '../pages/wireframes/TeamConfigPage'
import ClientSettingsPage from '../pages/wireframes/ClientSettingsPage'
import UserDetailPage from '../pages/wireframes/UserDetailPage'
import ScheduledJobsPage from '../pages/admin/ScheduledJobsPage'
import ScheduledJobCreatePage from '../pages/admin/ScheduledJobCreatePage'
import RevokeAuthorisationPage from '../pages/authorisation/revocation/RevokeAuthorisationPage'
import AuthorContentPage from '../pages/AuthorContentPage'
import ContentFeedPage from '../pages/ContentFeedPage'
import ContentPage from '../pages/ContentPage'
import { default as ContentSearchPage, default as ModeratePage } from '../pages/ContentSearchPage'
import HomePage from '../pages/HomePage'
import InstagramProfileConfigurationHelpPage from '../pages/InstagramProfileConfigurationHelpPage'
import NotificationConfigurationSettingsPage from '../pages/NotificationConfigurationSettingsPage'
import PromptTestingWrapperPage, { PromptTestingRedirect } from '../pages/PromptTestingWrapperPage'
import ReportHoldingPage from '../pages/reporting/ReportHoldingPage'
import AskArwenPage from '../pages/askArwen/AskArwenPage'
import { isGuidedOnboarding } from '../util/referrers'
import { GuidedOnboardingLayout } from './GuidedOnboardingLayout'
import ConnectStepPage from '../pages/guidedOnboarding/ConnectStepPage'
import ConfirmationStepPage from '../pages/guidedOnboarding/ConfirmationStepPage'
import DashboardPage from '../pages/guidedOnboarding/DashboardPage'
import { Toaster } from 'react-hot-toast'

export default function Main() {
    const location = useLocation()
    let navigate = useNavigate()

    useEffect(() => {
        // We use a HashRouter which isn't really supported by the Hosted UI used for SSO.
        // Following auth the Hosted UI redirects back to the main app with a token in the URL.
        // The Cognito/Amplify libs pick this up and use it for future auth but the resulting
        // URL confuses the HashRouter. This is a workaround to redirect to the correct URL.
        if (!location.pathname.startsWith('/')) {
            console.log('Redirecting to /')
            navigate('/')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])

    const query = useQueryParams()
    const [admin] = useRecoilState(isUserAdminState)

    const hasTwitterAuthParams = function () {
        return query.get('accountId') && query.get('source')
    }

    const hasFacebookAuthParams = function () {
        return query.get('state') && query.get('code')
    }

    const hasInstagramAuthParams = function () {
        return query.get('state') && query.get('code')
    }

    const hasYouTubeAuthParams = function () {
        return query.get('scope') && query.get('code')
    }

    const hasTikTokAdAuthParams = function () {
        return query.get('auth_code') && query.get('code')
    }

    const hasTikTokUserAuthParams = function () {
        return query.get('scopes') && query.get('code')
    }

    const hasAuthParams =
        hasTwitterAuthParams() ||
        hasFacebookAuthParams() ||
        hasInstagramAuthParams() ||
        hasYouTubeAuthParams() ||
        hasTikTokAdAuthParams() ||
        hasTikTokUserAuthParams()

    const homepage = function () {
        if (hasAuthParams) {
            /**
             * Following successful authorisation in an authorisation flow the redirect URL sends us back to the same domain the request derived from (e.g. app.arwen.ai or dev.arwen.ai)
             *
             * The redirect URL will contain a token and state information that differs for different social media services.
             *
             * This if block checks for the presence of auth query parameters and continues with the authorisation flow if they are present.
             */
            return <AuthoriseResponsePage />
        } else {
            return <HomePage />
        }
    }

    const [user] = useRecoilState(userState)

    return (
        <>
            <Routes>
                {isGuidedOnboarding(user.referrer) && (
                    <>
                        {hasAuthParams && <Route path="/" element={<AuthoriseResponsePage />} />}
                        <Route
                            // A hack to disable the guided onboarding layout on the homepage until we make the dashboard
                            element={<GuidedOnboardingLayout isAuthenticated />}
                        >
                            {/* A hack to disable the guided onboarding layout on the homepage until we make the dashboard */}
                            <Route path="/" element={<DashboardPage />}></Route>

                            <Route path="/connect" element={<ConnectStepPage />}></Route>
                            <Route
                                path="/authenticate"
                                element={<Navigate to="/" replace />}
                            ></Route>
                            <Route path="/confirmation" element={<ConfirmationStepPage />}></Route>
                            <Route
                                path={`account/new/twitter`}
                                element={<NewTwitterAccountPage />}
                            ></Route>
                            <Route
                                path={`account/new/instagram`}
                                element={<NewInstagramAccountFacebookAuthPage />}
                            ></Route>
                            <Route
                                path={`account/new/instagram_direct`}
                                element={<NewInstagramAccountPage />}
                            ></Route>
                            <Route
                                path={`account/new/facebook_page`}
                                element={<NewFacebookAccountPage />}
                            ></Route>
                            <Route
                                path={`account/new/youtube`}
                                element={<NewYouTubeAccountPage />}
                            ></Route>
                            <Route
                                path={`account/new/tiktok_ad`}
                                element={<NewTikTokAccountPage isAdsPage />}
                            ></Route>
                            <Route
                                path={`account/new/tiktok_user`}
                                element={<NewTikTokAccountPage />}
                            ></Route>
                            {admin ? (
                                <Route
                                    path={`account/new/monitored/:platform`}
                                    element={<NewMonitoredAccountPage />}
                                ></Route>
                            ) : null}
                            <Route
                                path="account/new"
                                element={<Navigate to="/connect" replace />}
                            ></Route>
                            {/* Redirect successful account adds to the confirmation page whilst preserving state and params */}
                            <Route
                                path="account/new/authorised"
                                element={
                                    <Navigate
                                        to={{ pathname: '/connect', search: location.search }}
                                        replace
                                        state={{
                                            ...(location?.state as any),
                                            newAccountAuthorised: true
                                        }}
                                    />
                                }
                            />
                        </Route>
                    </>
                )}

                <Route element={<Sidebar />}>
                    {user.shouldProvisionAccess && (
                        <>
                            <Route path="/" element={homepage()}></Route>
                            <Route
                                path="/author/:authorProfileId/account/:accountId"
                                element={<AuthorContentPage />}
                            />
                            <Route path="/content-search" element={<ModeratePage />}></Route>
                            <Route path="/account">
                                <Route path=":accountId">
                                    <Route index element={<AccountPage />}></Route>
                                    {/*Keywords are now user-facing*/}
                                    <Route path="keywords">
                                        <Route path="create" element={<KeywordCreatePage />} />
                                    </Route>
                                    {admin ? (
                                        <Route
                                            path="moderation-boundaries"
                                            element={<ModerationBoundariesPage />}
                                        />
                                    ) : null}
                                    {admin ? (
                                        <Route
                                            path="fetch-historic"
                                            element={<FetchHistoricContentPage />}
                                        />
                                    ) : null}

                                    <Route path="settings">
                                        {/* Settings are now accessible to all users BUT not all pages are allowed or visible */}
                                        <Route index element={<AccountSettingsPage />}></Route>
                                        <Route
                                            path="shadow-banning"
                                            element={<AccountShadowBanningSettingsPage />}
                                        />
                                        {admin ? (
                                            <>
                                                {/* These are still admin user only */}
                                                <Route
                                                    path="llm-reply"
                                                    element={<LlmReplySettingsPage />}
                                                />
                                                <Route
                                                    path="validation"
                                                    element={<AccountValidationPage />}
                                                />

                                                <Route
                                                    path="notifications"
                                                    element={
                                                        <NotificationConfigurationSettingsPage />
                                                    }
                                                />
                                                <Route
                                                    path="meta-ad-visibility"
                                                    element={<MetaAdVisibilityPage />}
                                                />
                                            </>
                                        ) : null}
                                    </Route>

                                    {/* SOMEDAY: we really want a single page to cover all authorisation issues.
                                        So we'll want to fold this into AuthoriseAccountPage */}
                                    <Route
                                        path="authorisation/revoke"
                                        element={<RevokeAuthorisationPage />}
                                    />
                                    <Route
                                        path="authorisation"
                                        element={<AccountAuthorisationPage />}
                                    />
                                    <Route
                                        path="unauthorised"
                                        element={<AuthorisationFailurePage />}
                                    />
                                </Route>
                                <Route
                                    path={`new/twitter`}
                                    element={<NewTwitterAccountPage />}
                                ></Route>
                                <Route
                                    path={`new/instagram`}
                                    element={<NewInstagramAccountFacebookAuthPage />}
                                ></Route>
                                <Route
                                    path={`new/instagram_direct`}
                                    element={<NewInstagramAccountPage />}
                                ></Route>
                                <Route
                                    path={`new/facebook_page`}
                                    element={<NewFacebookAccountPage />}
                                ></Route>
                                <Route
                                    path={`new/youtube`}
                                    element={<NewYouTubeAccountPage />}
                                ></Route>
                                <Route
                                    path={`new/tiktok_ad`}
                                    element={<NewTikTokAccountPage isAdsPage />}
                                ></Route>
                                <Route
                                    path={`new/tiktok_user`}
                                    element={<NewTikTokAccountPage />}
                                ></Route>
                                {admin ? (
                                    <Route
                                        path={`new/monitored/:platform`}
                                        element={<NewMonitoredAccountPage />}
                                    ></Route>
                                ) : null}
                                <Route
                                    path="new/authorised"
                                    element={<NewAccountAuthorisedPage />}
                                />
                                {/* This "new" route needs to come after the "new/<platform>" routes */}
                                <Route path="new" element={<NewAccountPage />}></Route>
                            </Route>

                            <Route path="/content-search" element={<ContentSearchPage />} />
                            <Route
                                path="/content-search/feed/:contentFeedId"
                                element={<ContentFeedPage />}
                            />
                            <Route path="/ask-arwen" element={<AskArwenPage />} />

                            <Route path={`/team`} element={<TeamPage />}></Route>

                            {/*
                                EN-327: the Reports tab now shows an interim holding page for
                                everyone while reporting moves to Monthly Moderation Reports. The
                                embedded Preset dashboard pages are no longer routed; the Preset
                                teardown is tracked under EN-318.
                            */}
                            <Route path="/report" element={<ReportHoldingPage />} />
                            <Route path="/report/*" element={<ReportHoldingPage />} />

                            {admin ? (
                                <>
                                    {/* Legacy top-level path — kept as a redirect so old
                                        links/bookmarks land in the new admin area. */}
                                    <Route
                                        path={`/spam-model`}
                                        element={<Navigate to="/admin/spam-model" replace />}
                                    />
                                    <Route path={`/admin`} element={<AdminLayout />}>
                                        {/* Landing on /admin drops you on the first item. */}
                                        <Route index element={<Navigate to="team" replace />} />
                                        {/*
                                         * Wireframe pages surfaced inside the real admin IA so proposed
                                         * designs can be reviewed against the live navigation. They render
                                         * the same components as the /wireframes hub — test-only branch,
                                         * not intended to ship as-is.
                                         */}
                                        <Route
                                            path={`classifiers`}
                                            element={<AdminClassifierLibraryPage />}
                                        />
                                        <Route
                                            path={`classifiers/new`}
                                            element={<CreateClassifierPage />}
                                        />
                                        <Route
                                            path={`classifiers/:id/edit`}
                                            element={<AdminClassifierEditPage />}
                                        />
                                        <Route path={`organisations`} element={<OrgListPage />} />
                                        <Route
                                            path={`spam-model`}
                                            element={<SpamModelPage />}
                                        ></Route>
                                        <Route path={`team`} element={<TeamListPage />}></Route>
                                        <Route
                                            path={`team/create`}
                                            element={<EnhancedTeamDetailsForm />}
                                        ></Route>
                                        {/* SOMEDAY: should be team/:teamId/edit */}
                                        <Route
                                            path={`team/edit/:teamId`}
                                            element={<TeamEditPage />}
                                        ></Route>
                                        <Route path={`user/`} element={<UserListPage />}></Route>
                                        <Route
                                            path={`user/create`}
                                            element={<UserCreatePage />}
                                        ></Route>
                                        {/* SOMEDAY: should be user/:userId/edit */}
                                        <Route
                                            path={`user/edit/:userId`}
                                            element={<UserEditPage />}
                                        ></Route>
                                        <Route
                                            path={`user/events/:userId`}
                                            element={<UserEventsPage />}
                                        ></Route>
                                        <Route
                                            path={`commenter/`}
                                            element={<CommenterListPage />}
                                        ></Route>
                                        <Route path={`post`} element={<PostListPage />} />
                                        <Route path={`post/:postId`} element={<PostDetailPage />} />
                                        <Route
                                            path={`prompt-testing`}
                                            element={<PromptTestingRedirect />}
                                        />
                                        <Route
                                            path={`prompt-testing/text`}
                                            element={<PromptTestingWrapperPage />}
                                        />
                                        <Route
                                            path={`prompt-testing/image`}
                                            element={<PromptTestingWrapperPage />}
                                        />
                                        <Route
                                            path={`service-health`}
                                            element={<ServiceHealthPage />}
                                        ></Route>
                                        <Route path={`cookie`} element={<CookieListPage />} />
                                        <Route path={`cookie/new`} element={<CookieFormPage />} />
                                        <Route
                                            path={`cookie/:cookieId/edit`}
                                            element={<CookieFormPage />}
                                        />
                                        <Route path={`referrer`} element={<ReferrerListPage />} />
                                        <Route path={`screenshot`} element={<ScreenshotListPage />} />
                                        {/* The scheduledWork API is admin-only, so the
                                            page sits inside this admin-gated route block. */}
                                        <Route
                                            path={`scheduled-jobs`}
                                            element={<ScheduledJobsPage />}
                                        />
                                        <Route
                                            path={`scheduled-jobs/new`}
                                            element={<ScheduledJobCreatePage />}
                                        />
                                    </Route>
                                </>
                            ) : null}

                            {/*
                             * The standalone wireframes hub and its prototype screens. Admin-gated
                             * and test-only — the admin-IA routes above render the same components,
                             * while these carry the internal links between prototype screens.
                             */}
                            {admin ? (
                                <>
                                    {/*
                                     * Wireframe page surfaced in the live (non-admin) IA so the proposed
                                     * read-only client view of Org Config can be reviewed against the real
                                     * navigation. Renders the same component as /wireframes/org/client —
                                     * test-only branch, not intended to ship as-is.
                                     */}
                                    <Route path={`/policies`} element={<ClientSettingsPage />} />

                                    <Route path={`/wireframes`}>
                                        <Route index element={<WireframesHubPage />} />
                                        <Route path={`classifiers`} element={<ClassifierLibraryPage />} />
                                        <Route path={`classifiers/edit`} element={<ClassifierEditPage />} />
                                        <Route path={`orgs`} element={<OrgListPage />} />
                                        <Route path={`org`} element={<OrgConfigPage />} />
                                        <Route
                                            path={`org/users`}
                                            element={<OrgConfigPage initialTab="Users & access" />}
                                        />
                                        <Route path={`org/user/:userId`} element={<UserDetailPage />} />
                                        <Route path={`org/client`} element={<ClientSettingsPage />} />
                                        <Route path={`profile`} element={<ProfileConfigPage />} />
                                        <Route path={`team`} element={<TeamConfigPage />} />
                                    </Route>
                                </>
                            ) : null}

                            <Route
                                path="/error-adding-instagram"
                                element={<AddInstagramErrorPage />}
                            ></Route>

                            <Route path="not-found" element={<NotFoundPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </>
                    )}

                    <Route path="/content/:contentId" element={<ContentPage />} />
                    <Route path="/about" element={<AboutPage />}></Route>
                    <Route
                        path="/support/instagram-profile-configuration/"
                        element={<InstagramProfileConfigurationHelpPage />}
                    ></Route>
                    <Route path="/support" element={<SupportPage />}></Route>
                </Route>
            </Routes>
            <div>
                <Toaster />
            </div>
            <Tooltip
                id="tooltip-id"
                delayShow={300}
                className="overflow-hidden"
                style={{ zIndex: 12000 }}
            />
        </>
    )
}
